/**
 * Beweiskette eines Vertrags: Abschluss-Hash nachrechnen, Zeitpunkte in UTC mit Mikrosekunden (wie die Datenbank),
 * Vertretungsbefugnis beim Unterschreiben. Gleich halten mit app.contract_final_hash (0040).
 */
import type { ContractRole } from "./contract-variables.js";
import type { Language } from "./types.js";

/** Reihenfolge der Rollen in der Datenbank (Aufzählungstyp signer_role): Agentur, Creator, Marke. */
export const SIGNER_ROLE_DB_ORDER: readonly ContractRole[] = ["agency", "talent", "brand"];

/**
 * Zeitpunkt (ISO mit beliebigem Versatz, Mikrosekunden erlaubt) als „YYYY-MM-DDTHH:MM:SS.ffffffZ“ in UTC.
 * Die Nachkommastellen bleiben exakt erhalten (Date kennt nur Millisekunden).
 */
export function utcMicros(ts: string | null | undefined): string | null {
  if (!ts) return null;
  const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})(?:\.(\d{1,6}))?\s*(Z|[+-]\d{2}(?::?\d{2})?)?$/.exec(ts.trim());
  if (!m) return null;
  const [, day, time, frac = "", zone = "Z"] = m;
  const offset = zone === "Z" ? "Z" : zone.length === 3 ? `${zone}:00` : zone.includes(":") ? zone : `${zone.slice(0, 3)}:${zone.slice(3)}`;
  const d = new Date(`${day}T${time}${offset}`);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.toISOString().slice(0, 19)}.${frac.padEnd(6, "0")}Z`;
}

export interface HashSigner {
  role: ContractRole;
  slot: number;
  signing_name: string | null;
  signed_at: string | null;
  /** nur gültige Unterschriften zählen (nicht ungültig gemacht) */
  valid: boolean;
}

/** Eingabe des Abschluss-Hashes: Text + Anlage + je Unterschrift „Rolle#Platz:Name:Zeitpunkt (UTC)“, getrennt durch |. */
export function finalHashInput(html: string | null, annex: string | null, signers: readonly HashSigner[]): string {
  const rank = (r: ContractRole) => SIGNER_ROLE_DB_ORDER.indexOf(r);
  const parts = signers
    .filter((s) => s.valid && s.signed_at)
    .sort((a, b) => rank(a.role) - rank(b.role) || a.slot - b.slot)
    .map((s) => `${s.role}#${s.slot}:${s.signing_name ?? ""}:${utcMicros(s.signed_at) ?? ""}`);
  return (html ?? "") + (annex ?? "") + parts.join("|");
}

export type SigningAuthority = "managing_director" | "procuration" | "power_of_attorney" | "authorized";
export const SIGNING_AUTHORITIES: readonly SigningAuthority[] = ["managing_director", "procuration", "power_of_attorney", "authorized"];

/** Vertretungsbefugnis mit dem Zusatz vor dem Namen (ppa., i. V.). */
export const AUTHORITY_LABEL: Readonly<Record<SigningAuthority, { de: string; en: string; prefix: { de: string; en: string } }>> = {
  managing_director: { de: "Geschäftsführung", en: "Managing director", prefix: { de: "", en: "" } },
  procuration: { de: "Prokura", en: "Procuration (Prokura)", prefix: { de: "ppa.", en: "p.p." } },
  power_of_attorney: { de: "Handlungsvollmacht", en: "Power of attorney", prefix: { de: "i. V.", en: "on behalf" } },
  authorized: { de: "Vertretungsberechtigt", en: "Authorised signatory", prefix: { de: "", en: "" } },
};

/** Name mit Zusatz der Befugnis, z. B. „ppa. Max Beispiel“. */
export function signedAsName(name: string, authority: string | null | undefined, lang: Language): string {
  const a = authority && (SIGNING_AUTHORITIES as readonly string[]).includes(authority) ? AUTHORITY_LABEL[authority as SigningAuthority] : null;
  const prefix = a?.prefix[lang] ?? "";
  return prefix ? `${prefix} ${name}` : name;
}

/**
 * Funktion und Befugnis unter der Unterschrift (Prüf-Chat Nachtest 03.11.2026, C): gleiche Angaben nur einmal, ohne Rücksicht auf
 * Groß- und Kleinschreibung („Managing Director, Managing director“ wird „Managing Director“). Leer: null.
 */
export function funktionUndBefugnis(teile: readonly (string | null | undefined)[]): string | null {
  const out: string[] = [];
  for (const t of teile) {
    const v = (t ?? "").trim();
    if (v && !out.some((x) => x.toLocaleLowerCase() === v.toLocaleLowerCase())) out.push(v);
  }
  return out.length ? out.join(", ") : null;
}
